/**
 * atomberg-carousel-arrows.js
 * ─────────────────────────────
 * Scope-based carousel arrows.
 * - Arrows must live inside an ancestor with `data-carousel-scope`.
 * - Scrollable tracks inside the scope are tagged `data-carousel-track`.
 * - On click, the JS finds the currently-visible track within the scope and scrolls it.
 * - Listens to `atomberg:tab-change` event on the scope to re-bind when panels swap.
 * - A scope with `data-carousel-infinite` wraps to the other end at the
 *   boundaries instead of disabling the arrows.
 */
(function () {
  if (/** @type {any} */ (window)._atombergCarouselArrowsInit) return;
  /** @type {any} */ (window)._atombergCarouselArrowsInit = true;

  var SCROLL_THRESHOLD = 4;

  /**
   * @param {HTMLElement} track
   * @param {HTMLButtonElement} prev
   * @param {HTMLButtonElement} next
   * @param {boolean} [infinite]
   */
  function updateArrows(track, prev, next, infinite) {
    var scrollLeft = track.scrollLeft;
    var maxScroll = track.scrollWidth - track.clientWidth;
    if (infinite) {
      prev.disabled = maxScroll <= 0;
      next.disabled = maxScroll <= 0;
      return;
    }
    prev.disabled = scrollLeft <= SCROLL_THRESHOLD;
    next.disabled = maxScroll <= 0 || scrollLeft >= maxScroll - SCROLL_THRESHOLD;
  }

  /**
   * @param {HTMLElement} wrapper
   */
  function initArrows(wrapper) {
    var scopeMaybe = /** @type {HTMLElement|null} */ (wrapper.closest('[data-carousel-scope]'));
    if (!scopeMaybe) return;
    var scope = /** @type {HTMLElement} */ (scopeMaybe);

    var prev = /** @type {HTMLButtonElement|null} */ (
      wrapper.querySelector('.atomberg-arrow--prev')
    );
    var next = /** @type {HTMLButtonElement|null} */ (
      wrapper.querySelector('.atomberg-arrow--next')
    );
    if (!prev || !next) return;

    var infinite = scope.hasAttribute('data-carousel-infinite');

    /** Find the visible track within the scope. */
    function getActiveTrack() {
      var tracks = /** @type {NodeListOf<HTMLElement>} */ (
        scope.querySelectorAll('[data-carousel-track]')
      );
      for (var i = 0; i < tracks.length; i++) {
        var t = tracks[i];
        if (t && t.offsetParent !== null) return t;
      }
      return tracks[0] || null;
    }

    /**
     * @param {number} direction
     */
    function scroll(direction) {
      var track = getActiveTrack();
      if (!track) return;
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (infinite && direction > 0 && track.scrollLeft >= maxScroll - SCROLL_THRESHOLD) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      if (infinite && direction < 0 && track.scrollLeft <= SCROLL_THRESHOLD) {
        track.scrollTo({ left: maxScroll, behavior: 'smooth' });
        return;
      }
      track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
    }

    var p = /** @type {HTMLButtonElement} */ (prev);
    var n = /** @type {HTMLButtonElement} */ (next);

    prev.addEventListener('click', function () {
      scroll(-1);
    });
    next.addEventListener('click', function () {
      scroll(1);
    });

    var rafId = 0;

    function bindActiveTrack() {
      var track = getActiveTrack();
      if (!track) return;
      updateArrows(track, p, n, infinite);

      // Avoid stacking listeners on the same track across re-binds
      var marked = /** @type {any} */ (track)._atombergScrollBound;
      if (marked) return;
      /** @type {any} */ (track)._atombergScrollBound = true;

      track.addEventListener('scroll', function () {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(function () {
          updateArrows(/** @type {HTMLElement} */ (track), p, n, infinite);
        });
      });
    }

    bindActiveTrack();

    // Re-evaluate when tabs swap inside the scope
    scope.addEventListener('atomberg:tab-change', bindActiveTrack);

    // Re-evaluate on resize (track width changes)
    window.addEventListener('resize', function () {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        var track = getActiveTrack();
        if (track) updateArrows(track, p, n, infinite);
      });
    });
  }

  function start() {
    document.querySelectorAll('[data-carousel-arrows]').forEach(function (el) {
      initArrows(/** @type {HTMLElement} */ (el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
